import { tokenAmount } from '@/features/trading/utils/tradeFormat';
import { type PublicTapeTrade, USDC_DECIMALS } from '@/lib/api/speed';

/**
 * What the viewer has narrowed the tape to.
 *
 * All of these except `trader` filter the rows the page already holds — at most
 * MAX_ROWS of them — and that is the honest scope: the tape is a live feed, not
 * a searchable history. Status, direction, coin, size and pnl all match a
 * decent fraction of any few-hundred-row window, so filtering it is useful.
 *
 * `trader` is the exception and is served server-side (`GET /speed/live?trader=`
 * plus its own index), because one trader's rows among the last few hundred
 * GLOBAL trades is almost always none. It stays in this predicate anyway: the
 * tape socket pushes everyone's trades to every viewer, with no per-connection
 * subscription, so without it a trader view would refill with strangers.
 */
export type StatusFilter = 'all' | 'open' | 'closed';
export type DirectionFilter = 'all' | 'long' | 'short';
export type PnlFilter = 'all' | 'winners' | 'losers';

export interface TapeFilter {
    status: StatusFilter;
    direction: DirectionFilter;
    /** market symbol, either case */
    coin: string | null;
    /** minimum collateral in DOLLARS, compared against the row's own margin */
    minMargin: number | null;
    pnl: PnlFilter;
    /** adapter address; also sent to the server so it can reach past the window */
    trader: string | null;
}

export const NO_FILTER: TapeFilter = Object.freeze({
    status: 'all',
    direction: 'all',
    coin: null,
    minMargin: null,
    pnl: 'all',
    trader: null,
});

const STATUSES: readonly StatusFilter[] = ['all', 'open', 'closed'];
const DIRECTIONS: readonly DirectionFilter[] = ['all', 'long', 'short'];
const PNLS: readonly PnlFilter[] = ['all', 'winners', 'losers'];

const ADDRESS = /^0x[0-9a-f]{40}$/i;

/**
 * How many groups are narrowing the feed. A phone keeps these behind a button,
 * and a shut sheet hides its own state — without a number on the button a
 * viewer cannot tell a quiet tape from a filtered one.
 */
export function activeFilterCount(filter: TapeFilter): number {
    return (
        Number(filter.status !== 'all') +
        Number(filter.direction !== 'all') +
        Number(filter.coin !== null) +
        Number(filter.minMargin !== null) +
        Number(filter.pnl !== 'all') +
        Number(filter.trader !== null)
    );
}

/** Whether anything is narrowed, i.e. whether the row count needs qualifying. */
export function isFiltered(filter: TapeFilter): boolean {
    return activeFilterCount(filter) > 0;
}

/**
 * Every selected filter must match. A row that simply does not record what a
 * filter asks about — no side, no market, no margin — is excluded rather than
 * admitted: "unknown" is not "big", and a row with no side is not a long.
 */
export function matchesTapeFilter(
    trade: PublicTapeTrade,
    filter: TapeFilter,
): boolean {
    const isOpen = trade.status === 'Open';

    if (filter.status === 'open' && !isOpen) return false;
    // every terminal status counts as closed, not just the one called Closed
    if (filter.status === 'closed' && isOpen) return false;

    if (
        filter.direction !== 'all' &&
        trade.isLong !== (filter.direction === 'long')
    ) {
        return false;
    }

    // the tape stores coins lowercased and the markets list does not; match on
    // neither side's casing, the same way markPricesByCoin does
    if (
        filter.coin &&
        trade.coin?.toUpperCase() !== filter.coin.toUpperCase()
    ) {
        return false;
    }

    if (
        filter.trader &&
        trade.adapterAddress.toLowerCase() !== filter.trader.toLowerCase()
    ) {
        return false;
    }

    if (filter.minMargin !== null) {
        // margin arrives in USDC base units; the filter is in dollars, which is
        // what the row displays and therefore what the viewer typed
        const margin = tokenAmount(trade.margin, USDC_DECIMALS);
        if (margin === null || margin < filter.minMargin) return false;
    }

    if (filter.pnl !== 'all') {
        // An open row's figure is derived from the live mark and moves every
        // tick, so filtering on it would make rows appear and vanish as prices
        // move. A pnl filter is a filter on settled trades; the chip says so.
        if (isOpen || trade.pnl === null) return false;
        const pnl = Number(trade.pnl);
        if (!Number.isFinite(pnl)) return false;
        // flat is neither a win nor a loss
        if (filter.pnl === 'winners' ? pnl <= 0 : pnl >= 0) return false;
    }

    return true;
}

function oneOf<T extends string>(
    value: string | null,
    allowed: readonly T[],
    fallback: T,
): T {
    return allowed.includes(value as T) ? (value as T) : fallback;
}

/**
 * Read the filter out of the URL. Junk falls back to "no filter" rather than
 * erroring or matching nothing: a mistyped link should show the tape, not an
 * empty page the viewer cannot explain.
 */
export function parseTapeFilter(params: URLSearchParams): TapeFilter {
    const rawMin = params.get('min');
    const min = rawMin === null ? Number.NaN : Number(rawMin);
    const trader = params.get('trader');

    return {
        status: oneOf(params.get('status'), STATUSES, 'all'),
        direction: oneOf(params.get('dir'), DIRECTIONS, 'all'),
        // Uppercased to the form the market controls list. The predicate
        // ignores case, so a mismatched one still filters rows correctly while
        // matching no option in the picker — which renders as an empty trigger
        // rather than anything a viewer could diagnose.
        coin: params.get('coin')?.trim().toUpperCase() || null,
        minMargin: Number.isFinite(min) && min > 0 ? min : null,
        pnl: oneOf(params.get('pnl'), PNLS, 'all'),
        trader: trader && ADDRESS.test(trader) ? trader.toLowerCase() : null,
    };
}

/** The inverse. Absent keys for anything unset, so a clean tape has a clean URL. */
export function tapeFilterParams(filter: TapeFilter): Record<string, string> {
    const params: Record<string, string> = {};
    if (filter.status !== 'all') params.status = filter.status;
    if (filter.direction !== 'all') params.dir = filter.direction;
    if (filter.coin) params.coin = filter.coin;
    if (filter.minMargin !== null) params.min = String(filter.minMargin);
    if (filter.pnl !== 'all') params.pnl = filter.pnl;
    if (filter.trader) params.trader = filter.trader;
    return params;
}
